import { useState, useContext, Fragment, useEffect } from 'react';

import {
  Button,
  CssBaseline,
  TextField,
  Paper,
  Box,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
} from '@mui/material';

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import SecurityIcon from '@mui/icons-material/Security';

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthContext } from '../context/AuthProvider';
import axios from '../api/axios';
import { BrowserTitle } from '../components';

const buttonStyles = {
  mt: 3,
  mb: 2,
  p: 1.25,
};

const TRUSTED_DEVICE_KEY = 'airmaxx_trusted_device';

export const Login = () => {
  const auth = useContext(AuthContext);
  const [isLoginPage, setPageMode] = useState(true);
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFactorData, setTwoFactorData] = useState(null);
  const [loginCredentials, setLoginCredentials] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const trustedDeviceToken = localStorage.getItem(TRUSTED_DEVICE_KEY);

    console.log('[Login] Attempting login, trustedDeviceToken:', trustedDeviceToken ? 'present' : 'none');

    try {
      const response = await axios.post('/auth/login', {
        email: formData.get('email')?.toString().toLowerCase(),
        password: formData.get('password'),
        trustedDeviceToken: trustedDeviceToken || undefined,
      });

      console.log('[Login] Response:', response.data);

      if (response.data.requiresTwoFactor) {
        // 2FA required
        setTwoFactorData({
          tempToken: response.data.tempToken,
          userId: response.data.userId,
        });
        setLoginCredentials({
          email: formData.get('email')?.toString().toLowerCase(),
          password: formData.get('password'),
        });
        setRequires2FA(true);
      } else if (response.data.token) {
        // Normal login
        auth.login(response.data.token, response.data.refreshToken);
      }
    } catch (error) {
      console.log(error);
      toast.error(error.response?.data?.message || 'Login failed');
    }
  };

  const handlePasswordRecover = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);

    try {
      const response = await axios.post('/auth/passwordRecovery', {
        email: formData.get('email')?.toString().toLowerCase(),
      });

      if (response.data) {
        toast.success(response.data.message);
        setPageMode(true);
      }
    } catch (error) {
      console.log(error);
      toast.error(error.response?.data?.message || 'Failed to send recovery email');
    }
  };

  const handleBack = () => {
    setRequires2FA(false);
    setTwoFactorData(null);
    setLoginCredentials(null);
  };

  const handleLoginSuccess = (token, refreshToken, trustedDeviceToken) => {
    console.log('[Login] handleLoginSuccess called, trustedDeviceToken:', trustedDeviceToken ? 'present' : 'none');
    if (trustedDeviceToken) {
      localStorage.setItem(TRUSTED_DEVICE_KEY, trustedDeviceToken);
      console.log('[Login] Saved trustedDeviceToken to localStorage');
    }
    auth.login(token, refreshToken);
  };

  return (
    <Grid container component="main" sx={{ height: '100vh' }}>
      <CssBaseline />
      <Grid
        item
        xs={false}
        sm={4}
        md={8}
        sx={{
          backgroundImage: 'url(../images/login_bg.jpeg)',
          backgroundRepeat: 'no-repeat',
          backgroundColor: (t) => (t.palette.mode === 'light' ? t.palette.grey[50] : t.palette.grey[900]),
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />

      <Grid item xs={12} sm={8} md={4} component={Paper} elevation={6} square sx={{ backgroundColor: '#F0F3F5' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%',
            padding: '0 60px',
          }}
        >
          <Box sx={{ mb: 2 }}>
            <img src="/images/Airmaxx-Logo-Trans.png" alt="Logo" style={{ height: 60 }} />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
            }}
          >
            <Typography
              component="h1"
              variant="h5"
              textAlign="left"
              sx={{
                fontFamily: '"Days One", sans-serif',
                fontSize: 24,
                color: '#0D3B59',
              }}
            >
              {requires2FA ? 'Two-Factor Authentication' : isLoginPage ? 'Sign In' : 'Reset your password'}
            </Typography>
            {!isLoginPage && !requires2FA && (
              <Typography
                component="p"
                variant="caption"
                sx={{
                  fontFamily: '"Lato", sans-serif',
                  fontSize: 14,
                }}
              >
                Enter the email address associated with your account.
              </Typography>
            )}
            {requires2FA && (
              <Typography
                component="p"
                variant="caption"
                sx={{
                  fontFamily: '"Lato", sans-serif',
                  fontSize: 14,
                }}
              >
                Enter the 6-digit code from your authenticator app.
              </Typography>
            )}
          </Box>
          <Box
            component="form"
            noValidate
            onSubmit={isLoginPage && !requires2FA ? handleSubmit : handlePasswordRecover}
            sx={{ mt: 1, width: '100%' }}
          >
            {requires2FA ? (
              <TwoFactorForm 
                twoFactorData={twoFactorData} 
                onBack={handleBack}
                onSuccess={handleLoginSuccess}
              />
            ) : isLoginPage ? (
              <SignInForm setPageMode={setPageMode} />
            ) : (
              <ForgotPasswordForm setPageMode={setPageMode} />
            )}
          </Box>
        </Box>
      </Grid>
      <ToastContainer />
    </Grid>
  );
};

const SignInForm = ({ setPageMode }) => {
  const [isVisible, setVisibility] = useState(false);
  const [isDisabled, setDisabled] = useState({
    email: false,
    password: false,
  });

  const handleMouseDownPassword = (event) => event.preventDefault();

  const handleChange = (event) => {
    const key = event.target.name;
    const val = event.target.value;

    if ((key === 'email' && val) || (key === 'password' && val)) {
      setDisabled((prevState) => ({
        ...prevState,
        [key]: true,
      }));
    } else {
      setDisabled((prevState) => ({
        ...prevState,
        [key]: false,
      }));
    }
  };

  return (
    <Fragment>
      <BrowserTitle title="Login | Airmaxx Pro" />

      <TextField
        margin="normal"
        required
        fullWidth
        id="email"
        label="Email Address"
        name="email"
        autoComplete="email"
        onChange={handleChange}
        sx={{ backgroundColor: '#fff', borderRadius: '10px' }}
      />

      <FormControl sx={{ mt: 2, width: '100%' }} variant="outlined">
        <InputLabel htmlFor="password">Password *</InputLabel>
        <OutlinedInput
          onChange={handleChange}
          id="password"
          name="password"
          type={isVisible ? 'text' : 'password'}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => setVisibility(!isVisible)}
                onMouseDown={handleMouseDownPassword}
                edge="end"
              >
                {isVisible ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
          label="Password *"
          sx={{ backgroundColor: '#fff' }}
        />
      </FormControl>

      <Button
        type="submit"
        fullWidth
        variant="contained"
        sx={{ ...buttonStyles, fontFamily: '"Lato", sans-serif', textTransform: 'none' }}
        disabled={!isDisabled.email || !isDisabled.password}
      >
        Sign In
      </Button>
      <Grid container>
        <Grid item xs sx={{ textAlign: 'center' }}>
          <Button
            variant="text"
            onClick={() => setPageMode(false)}
            sx={{ fontFamily: '"Lato", sans-serif', textTransform: 'none' }}
          >
            Forgot password?
          </Button>
        </Grid>
      </Grid>
    </Fragment>
  );
};

const TwoFactorForm = ({ twoFactorData, onBack, onSuccess }) => {
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (code.length < 6) return;

    setLoading(true);
    setError('');

    try {
      const response = await axios.post('/auth/verify-2fa', {
        tempToken: twoFactorData.tempToken,
        userId: twoFactorData.userId,
        token: code,
        trustDevice,
        deviceName: trustDevice ? getBrowserName() : undefined,
      });

      if (response.data.token) {
        onSuccess(response.data.token, response.data.refreshToken, response.data.trustedDeviceToken);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid code. Please try again.');
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  const getBrowserName = () => {
    const ua = navigator.userAgent;
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Edge')) return 'Edge';
    return 'Unknown Browser';
  };

  return (
    <Fragment>
      <BrowserTitle title="2FA Verification | Airmaxx Pro" />

      <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
        <SecurityIcon sx={{ fontSize: 48, color: '#0D3B59' }} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        margin="normal"
        required
        fullWidth
        id="2fa-code"
        label={useBackupCode ? "Backup Code" : "Verification Code"}
        name="code"
        autoComplete="one-time-code"
        autoFocus
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, useBackupCode ? 8 : 6))}
        inputProps={{ 
          maxLength: useBackupCode ? 8 : 6,
          style: { textAlign: 'center', fontSize: 24, letterSpacing: 8 }
        }}
        placeholder={useBackupCode ? "XXXXXXXX" : "000000"}
        sx={{ backgroundColor: '#fff', borderRadius: '10px' }}
      />

      <FormControlLabel
        control={
          <Checkbox 
            checked={trustDevice} 
            onChange={(e) => setTrustDevice(e.target.checked)}
            color="primary"
          />
        }
        label="Trust this device"
        sx={{ mt: 1, fontFamily: '"Lato", sans-serif' }}
      />

      <Button
        type="button"
        fullWidth
        variant="contained"
        onClick={handleSubmit}
        disabled={code.length < 6 || loading}
        sx={{ ...buttonStyles, fontFamily: '"Lato", sans-serif', textTransform: 'none' }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify'}
      </Button>

      <Grid container spacing={1}>
        <Grid item xs={6} sx={{ textAlign: 'center' }}>
          <Button
            variant="text"
            onClick={onBack}
            sx={{ fontFamily: '"Lato", sans-serif', textTransform: 'none' }}
          >
            Back to Login
          </Button>
        </Grid>
        <Grid item xs={6} sx={{ textAlign: 'center' }}>
          <Button
            variant="text"
            onClick={() => {
              setUseBackupCode(!useBackupCode);
              setCode('');
            }}
            sx={{ fontFamily: '"Lato", sans-serif', textTransform: 'none' }}
          >
            {useBackupCode ? 'Use App Code' : 'Use Backup Code'}
          </Button>
        </Grid>
      </Grid>
    </Fragment>
  );
};

const ForgotPasswordForm = ({ setPageMode }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await axios.post('/auth/forgot-password', { email: email.toLowerCase() });
      setSent(true);
      toast.success('Password reset link sent! Check your email.');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <Fragment>
        <BrowserTitle title="Reset Password | Airmaxx Pro" />
        
        <Alert severity="success" sx={{ mb: 2 }}>
          If an account with that email exists, you will receive a password reset link shortly.
        </Alert>
        
        <Typography variant="body2" sx={{ mb: 2, textAlign: 'center' }}>
          Please check your email (including spam folder) and follow the instructions.
        </Typography>

        <Button
          fullWidth
          variant="outlined"
          onClick={() => setPageMode(true)}
          sx={{ ...buttonStyles, fontFamily: '"Lato", sans-serif', textTransform: 'none' }}
        >
          Back to Login
        </Button>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <BrowserTitle title="Reset Password | Airmaxx Pro" />

      <TextField
        margin="normal"
        required
        fullWidth
        id="email"
        label="Email Address"
        name="email"
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        sx={{ backgroundColor: '#fff' }}
      />

      <Button
        type="button"
        fullWidth
        variant="contained"
        onClick={handleSubmit}
        sx={{ ...buttonStyles, fontFamily: '"Lato", sans-serif', textTransform: 'none' }}
        disabled={!email || loading}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Link'}
      </Button>
      <Grid container>
        <Grid item xs sx={{ textAlign: 'center' }}>
          <Button
            variant="text"
            onClick={() => setPageMode(true)}
            sx={{ fontFamily: '"Lato", sans-serif', textTransform: 'none' }}
          >
            Back to Login
          </Button>
        </Grid>
      </Grid>
    </Fragment>
  );
};
